import { all, call, put, takeLatest, takeEvery } from "redux-saga/effects";
import { pushNotification } from "utils/notifications";
import {
  getGPSHistoricalData,
  getRXData,
  getVideoSnapshot,
  getMapPlaces,
  getLocationProfile,
  getUsersAggregateList,
  getMediaList,
} from "../services/apis/collections/oAuthApis";
import {
  GET_HISTORICAL_GPS,
  GET_RX,
  GET_VIDEO_SNAPSHOT,
  GET_MAP_PLACES_BOUNDS,
  GET_MAP_PLACES_FILTER,
  GET_PAGE_PLACES_FILTER,
  GET_LOCATION_PROFILES,
} from "reducers/dashboardReducer";

function* GetGPSHistoricalDataAPI(action) {
  try {
    const { requestData, live, imei, messageId } = action.payload;
    const response = yield call(getGPSHistoricalData, requestData);
    if (response && response.status) {
      yield put({
        type: "ADD_GPS",
        payload: { response, live, imei, messageId },
      });
    } else {
      pushNotification(response, "error", "TOP_CENTER", 1000);
    }
  } catch (error: any) {
    console.log(error);
    pushNotification(error, "error", "TOP_CENTER", 1000);
  }
}

function* GetMapPlacesDataAPI(action) {
  try {
    const response = yield call(getMapPlaces, {
      ...action.payload.request,
    });
    if (response && response.data) {
      yield put({ type: "ADD_MAP_PLACES_BOUNDS", payload: response.data });
    } else {
      yield put({ type: "ADD_MAP_PLACES_BOUNDS", payload: [] });
    }
  } catch (error: any) {
    console.log(error);
    yield put({ type: "ADD_MAP_PLACES_BOUNDS", payload: [] });
  }
}

function* GetRXDataAPI(action) {
  try {
    const { id } = action.payload;
    const response = yield call(getRXData, id);
    if (response && response.status) {
      yield put({ type: "ADD_RX", payload: response.meta_data });
    } else {
      pushNotification(response, "error", "TOP_CENTER", 1000);
    }
  } catch (error: any) {
    console.log(error);
    pushNotification(error, "error", "TOP_CENTER", 1000);
  }
}

function* GetVideoSnapshotDataAPI(action) {
  try {
    const { id } = action.payload;
    const response = yield call(getVideoSnapshot, id);
    if (response && response.length > 0) {
      yield put({ type: "ADD_VIDEO_SNAPSHOT", payload: response[0] });
    } else {
      yield put({ type: "ADD_VIDEO_SNAPSHOT", payload: { success: false } });
    }
  } catch (error) {
    console.log(error);
    yield put({ type: "ADD_VIDEO_SNAPSHOT", payload: { success: false } });
  }
}

function* GetMapPlacesFilterDataAPI(action) {
  try {
    const { objectType } = action.payload;
    let response;
    if (
      objectType === "user" ||
      objectType === "gps" ||
      objectType === "page" ||
      objectType === "place" ||
      objectType === "gps" ||
      objectType === "asset"
    ) {
      response = yield call(getUsersAggregateList, {
        data: action.payload,
      });
    }
    else if (objectType === "media" || objectType === "event") {
      const pageListResponse = yield call(getUsersAggregateList, {
        data: {
          select: {
            index: {
              searchIndex: `${process.env.REACT_APP_ECO_SYSTEM}:pageTitleIndex`,
              searchQuery: `@pageMembers:{${localStorage.getItem("RID")}}`,
              searchOptions: "TIMEOUT 1000",
            },
            list: {},
            load: {
              serviceId: "$.serviceId",
            },
          },
          groupBy: {
            properties: ["@serviceId"],
            reducers: [
              {
                func: "TOLIST",
                arguments: ["@objectId"],
                name: "objectIds",
              },
            ],
          },
          apply: {
            expression: "",
            name: "",
          },
          result: {},
          filter: {
            expression: "",
          },
          anyStatus: false,
        },
      });

      const objectIds = pageListResponse?.data?.[0]?.objectIds || [];

      if (objectType === "media") {
        response = yield call(getMediaList, {
          data: {
            ...action.payload,
            searchQuery: `@pageId:{${objectIds.join("|")}}`,
          },
        });
      } else {
        const requestData = { ...action.payload };
        requestData.select.index.searchQuery = `@type:{event}@pageId:{${objectIds.join(
          "|"
        )}}`;
        response = yield call(getUsersAggregateList, {
          data: requestData,
        });
      }
    }
    if (response && response.data) {
      let responseData = response.data;

      if (objectType === "media") {
        responseData = response.data
          .map((item: { [x: string]: any[] }) => ({
            id: Object.keys(item)[0],
            obj: item[Object.keys(item)[0]][0],
          }))
          .filter(
            (item: { obj: {} }) => item.obj && Object.keys(item.obj).length > 0
          );
      }

      if (objectType === "user" || objectType === "gps") {

        responseData = response.data
          ?.filter((user) => {
            return user.email || user.phone;
          })
          ?.map((user) => {
            return {
              id: user.objectIds.toString(),
              name: user?.profile?.firstName + " " + user.profile?.lastName,
              firstName: user?.profile?.firstName ?? "",
              lastName: user?.profile?.lastName ?? "",
              gender: user?.profile?.gender ?? "",
              dob: user?.profile?.dob ?? "",
              email: user.email,
              phone: user.phone,
              beamId: user?.beamIds?.[0] || `${user?.profile?.firstName?.toLowerCase()}.${user?.profile?.lastName?.toLowerCase()}`,
              objectType,
              location: user?.profile?.location?.coordinates
                ? {
                    coordinates: [
                      user?.profile?.location.coordinates.long,
                      user?.profile?.location.coordinates.lat,
                    ],
                  }
                : undefined,
            };
          });
      } else if (objectType === "page") {
        responseData = responseData.map((page) => ({
          id: page.objectIds.toString(),
          name: page?.persistent?.static.metadata.title,
          objectType,
          location: page?.persistent?.static?.live?.location?.coordinates
            ? {
                coordinates: [
                  page?.persistent?.static?.live?.location.coordinates.long,
                  page?.persistent?.static?.live?.location.coordinates.lat,
                ],
              }
            : undefined,
        }));
      } else if (objectType === "place") {
        responseData = responseData.map((place) => ({
          id: place.objectIds.toString(),
          name: place?.persistent?.static?.poi?.location?.attributes?.title,
          objectType,
          location: place?.persistent?.static?.poi?.location?.coordinates
            ? {
                coordinates: [
                  place?.persistent?.static?.poi?.location.coordinates.long,
                  place?.persistent?.static?.poi?.location.coordinates.lat,
                ],
              }
            : undefined,
        }));
      } else if (objectType === "asset") {
        responseData = responseData.map((place) => ({
          id: place.objectIds.toString(),
          name: place?.persistent?.static?.asset?.location?.attributes?.title,
          objectType,
          location: place?.persistent?.static?.asset?.location?.coordinates
            ? {
                coordinates: [
                  place?.persistent?.static?.asset?.location.coordinates.long,
                  place?.persistent?.static?.asset?.location.coordinates.lat,
                ],
              }
            : undefined,
        }));
      } else if (objectType === "event") {
        responseData = responseData.map((event) => ({
          id: event.objectIds.toString(),
          name: event?.message?.attributes?.body,
          objectType,
          location: event?.message?.attributes?.location?.coordinates
            ? {
                coordinates: [
                  event?.message?.attributes?.location.coordinates.long,
                  event?.message?.attributes?.location.coordinates.lat,
                ],
              }
            : undefined,
        }));
      } else if (objectType === "media") {
        responseData = responseData.map((item: { id: any; obj: any[] }) => ({
          id: item.id,
          name: item.obj?.[0]?.originalName,
          objectType,
          location: item.obj?.[0]?.info?.location?.coordinates
            ? {
                coordinates: [
                  item.obj?.[0]?.info?.location.coordinates.long,
                  item.obj?.[0]?.info?.location.coordinates.lat,
                ],
              }
            : undefined,
          urls: item.obj?.[0]?.urls,
        }));
      }
      responseData = responseData.filter((user) => {
        return user.location !== undefined;
      });
      yield put({
        type: `ADD_MAP_${action.payload.objectType.toUpperCase()}_FILTER`,
        payload: responseData || [],
      });
    } else {
      pushNotification(response, "error", "TOP_CENTER", 1000);
    }
  } catch (error: any) {
    console.log(error);
    pushNotification(error, "error", "TOP_CENTER", 1000);
  }
}


function* GetMapPageFilterDataAPI(action) {
  try {
    const { requestData, callback } = action.payload;
    const {objectType} = requestData;
    const response = yield call(getUsersAggregateList, {
      data: requestData,
    });

    if (response && response.data) {
      let responseData = response.data;

        responseData = responseData.map((item) => ({
          id: item?.objects?.objectId,
          name: item.objects?.message?.attributes?.body,
          objectType,
          messageType: item.objects?.message?.type,
          location: item.objects?.message?.attributes?.coordinates
            ? {
                coordinates: [
                  item.objects?.message?.attributes?.coordinates?.long,
                  item.objects?.message?.attributes?.coordinates?.lat,
                ],
              }
            : (
              item.objects?.message?.attributes?.location?.coordinates ? 
              {
                coordinates: [
                  item.objects?.message?.attributes?.location?.coordinates?.long,
                  item.objects?.message?.attributes?.location?.coordinates?.lat,
                ],
              }: undefined
            ),
          data: item.objects?.message?.attributes,
          settings: item.objects?.message?.attributes,
          ...item?.objects?.pageInfo,
          urls: item.objects?.message?.attributes?.urls
        }));

      responseData = responseData.filter((user) => {
        return user.location !== undefined;
      });
      yield put({
        type: `ADD_MAP_${objectType.toUpperCase()}_FILTER`,
        payload: responseData || [],
      });
      if(callback)callback(responseData)
    } else {
      pushNotification(response, "error", "TOP_CENTER", 1000);
    }
  } catch (error: any) {
    console.log(error);
    pushNotification(error, "error", "TOP_CENTER", 1000);
  }
}


function* GetLocationProfilesDataAPI(action) {
  try {
    const response = yield call(getLocationProfile, action.payload);
    if (response && response.data) {
      yield put({ type: "SET_LOCATION_PROFILES", payload: response.data });
    } else {
      pushNotification(response, "error", "TOP_CENTER", 1000);
    }
  } catch (error: any) {
    console.log(error);
    pushNotification(error, "error", "TOP_CENTER", 1000);
  }
}

function* watchGetRequest() {
  yield takeEvery(GET_HISTORICAL_GPS, GetGPSHistoricalDataAPI);
  yield takeEvery(GET_RX, GetRXDataAPI);
  yield takeEvery(GET_VIDEO_SNAPSHOT, GetVideoSnapshotDataAPI);
  yield takeEvery(GET_MAP_PLACES_BOUNDS, GetMapPlacesDataAPI);
  yield takeEvery(GET_MAP_PLACES_FILTER, GetMapPlacesFilterDataAPI);
  yield takeEvery(GET_PAGE_PLACES_FILTER, GetMapPageFilterDataAPI);
  yield takeEvery(GET_LOCATION_PROFILES, GetLocationProfilesDataAPI);
}

export default function* sagas() {
  yield all([watchGetRequest()]);
}
